import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Building2,
  FileText,
  Award,
  Sparkles,
  Shield,
  Cpu,
  TrendingUp,
  CheckCircle2,
  ArrowRight,
  Landmark,
  Zap,
} from 'lucide-react';
import { Section, Container } from '@/components/ui/Section';
import { Card } from '@/components/ui/Card';
import { cn } from '@/lib/utils';

interface CaseStudy {
  id: string;
  client: string;
  title: string;
  description: string;
  results: string[];
  tags: string[];
  icon: React.ComponentType<{ className?: string }>;
  color: string;
  year: string;
}


export function Realizzazioni() {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language;
  const [selectedCase, setSelectedCase] = useState<CaseStudy | null>(null);
  const [activeFilter] = useState<'all' | 'pa' | 'enterprise' | 'security'>('all');

  const caseStudies: CaseStudy[] = [
    {
      id: 'consorzio-it',
      client: 'Consorzio.IT',
      title: t('realizzazioni.caseStudies.consorzio.title'),
      description: t('realizzazioni.caseStudies.consorzio.description'),
      results: t('realizzazioni.caseStudies.consorzio.results', { returnObjects: true }) as string[],
      tags: ['SemplicePA', 'AI', 'PA'],
      icon: Landmark,
      color: 'from-cta-400 to-cta-500',
      year: '2024',
    },
    {
      id: 'regione-umbria',
      client: 'Regione Umbria / PuntoZero',
      title: t('realizzazioni.caseStudies.umbria.title'),
      description: t('realizzazioni.caseStudies.umbria.description'),
      results: t('realizzazioni.caseStudies.umbria.results', { returnObjects: true }) as string[],
      tags: ['SemplicePA', 'BUR', 'Integration'],
      icon: FileText,
      color: 'from-secondary-400 to-secondary-500',
      year: '5+',
    },
    {
      id: 'acn',
      client: 'ACN - Agenzia Cybersicurezza',
      title: t('realizzazioni.caseStudies.acn.title'),
      description: t('realizzazioni.caseStudies.acn.description'),
      results: t('realizzazioni.caseStudies.acn.results', { returnObjects: true }) as string[],
      tags: ['Cybersecurity', 'FirmoSemplice', 'SempliceAI'],
      icon: Shield,
      color: 'from-primary-400 to-primary-500',
      year: '2025',
    },
    {
      id: 'maggioli-apkappa',
      client: 'Gruppo Maggioli & APKappa',
      title: t('realizzazioni.caseStudies.maggioli.title'),
      description: t('realizzazioni.caseStudies.maggioli.description'),
      results: t('realizzazioni.caseStudies.maggioli.results', { returnObjects: true }) as string[],
      tags: ['Partnership', 'AI', 'Protocol'],
      icon: Cpu,
      color: 'from-cta-400 to-secondary-500',
      year: '2024',
    },
    {
      id: 'milan-fintech',
      client: 'Milan Fintech Summit',
      title: t('realizzazioni.caseStudies.fintech.title'),
      description: t('realizzazioni.caseStudies.fintech.description'),
      results: t('realizzazioni.caseStudies.fintech.results', { returnObjects: true }) as string[],
      tags: ['FirmoSemplice', 'Fintech', 'Events'],
      icon: Building2,
      color: 'from-primary-400 to-cta-500',
      year: '2024',
    },
    {
      id: 'unipi-cnr',
      client: 'Università di Pisa & CNR',
      title: t('realizzazioni.caseStudies.unipi.title'),
      description: t('realizzazioni.caseStudies.unipi.description'),
      results: t('realizzazioni.caseStudies.unipi.results', { returnObjects: true }) as string[],
      tags: ['Research', 'NLP', 'Innovation'],
      icon: Sparkles,
      color: 'from-secondary-400 to-primary-500',
      year: '2024',
    },
  ];

  const stats = [
    { value: '5+', label: t('realizzazioni.stats.yearsLabel'), sublabel: t('realizzazioni.stats.yearsSublabel') },
    { value: '95%', label: t('realizzazioni.stats.reductionLabel'), sublabel: t('realizzazioni.stats.reductionSublabel') },
    { value: '<1', label: t('realizzazioni.stats.timeLabel'), sublabel: t('realizzazioni.stats.timeSublabel') },
    { value: '70%', label: t('realizzazioni.stats.savingsLabel'), sublabel: t('realizzazioni.stats.savingsSublabel') },
  ];

  const filteredCases =
    activeFilter === 'all'
      ? caseStudies
      : caseStudies.filter((c) =>
          c.tags.some((tag) =>
            activeFilter === 'pa'
              ? tag.includes('PA') || tag.includes('SemplicePA')
              : activeFilter === 'security'
                ? tag.includes('Cybersecurity') || tag.includes('Firmo')
                : tag.includes('Partnership') || tag.includes('Fintech')
          )
        );

  return (
    <>
      <Helmet>
        <title>{t('realizzazioni.pageTitle')}</title>
        <meta name="description" content={t('realizzazioni.metaDescription')} />
        <link rel="canonical" href={`https://www.01sistemi.it/${currentLang}/realizzazioni`} />
      </Helmet>

      {/* Hero - Pink Glassmorphism Style */}
      <Section className="relative overflow-hidden" padding="lg">
        {/* Background gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-pink-100/80 via-rose-50/90 to-pink-50/80" />
        <div className="absolute top-0 left-0 w-full h-full bg-white/30 backdrop-blur-sm" />

        {/* Decorative blobs */}
        <div className="absolute top-10 left-[10%] w-72 h-72 bg-pink-300/40 rounded-full blur-[100px]" />
        <div className="absolute bottom-20 right-[5%] w-96 h-96 bg-rose-200/30 rounded-full blur-[120px]" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-pink-100/50 rounded-full blur-[150px]" />

        <Container className="relative z-10">
          <div className="text-center max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <span className="inline-flex items-center gap-2 px-4 py-2 bg-white/70 backdrop-blur-sm rounded-full text-sm font-medium text-primary-600 border border-pink-200 shadow-sm">
                <Award className="w-4 h-4" />
                {t('realizzazioni.overline')}
              </span>
            </motion.div>

            <motion.h1
              className="heading-1 mt-6 text-4xl sm:text-5xl lg:text-6xl font-bold"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              {t('realizzazioni.heroTitle')}
            </motion.h1>

            <motion.p
              className="text-lg sm:text-xl text-gray-700 mt-6 max-w-2xl mx-auto leading-relaxed"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              {t('realizzazioni.heroDescription')}
            </motion.p>
          </div>

          {/* Stats Grid */}
          <motion.div
            className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-16"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            {stats.map((stat, index) => (
              <motion.div
                key={stat.label}
                className="bg-white/70 backdrop-blur-md rounded-2xl p-6 border border-pink-100 shadow-lg text-center"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 + index * 0.1 }}
                whileHover={{ y: -5, transition: { duration: 0.2 } }}
              >
                <div className="text-3xl sm:text-4xl font-bold bg-gradient-to-r from-primary-400 to-cta-500 bg-clip-text text-transparent">
                  {stat.value}
                </div>
                <div className="text-sm font-semibold text-gray-900 mt-1">{stat.label}</div>
                <div className="text-xs text-gray-500 mt-1">{stat.sublabel}</div>
              </motion.div>
            ))}
          </motion.div>
        </Container>
      </Section>

      {/* Case Studies Grid */}
      <Section padding="lg" className="relative">
        {/* Subtle background */}
        <div className="absolute inset-0 bg-gradient-to-b from-white via-pink-50/30 to-white" />

        <Container className="relative z-10">
          <motion.div
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-6"
            layout
          >
            <AnimatePresence mode="popLayout">
              {filteredCases.map((caseStudy, index) => {
                const Icon = caseStudy.icon;
                return (
                  <motion.div
                    key={caseStudy.id}
                    layout
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    transition={{ duration: 0.3, delay: index * 0.05 }}
                  >
                    <Card
                      variant="hover"
                      className="h-full cursor-pointer group bg-white/80 backdrop-blur-sm border border-pink-100/50 hover:border-pink-200 hover:shadow-xl hover:shadow-pink-100/50 transition-all duration-300"
                      onClick={() => setSelectedCase(caseStudy)}
                    >
                      {/* Header with gradient */}
                      <div
                        className={cn(
                          'h-2 w-full bg-gradient-to-r rounded-t-xl mb-5',
                          caseStudy.color
                        )}
                      />

                      <div className="px-5 pb-5">
                        {/* Icon and Year */}
                        <div className="flex items-center justify-between mb-4">
                          <div
                            className={cn(
                              'w-12 h-12 rounded-xl bg-gradient-to-br flex items-center justify-center',
                              caseStudy.color
                            )}
                          >
                            <Icon className="w-6 h-6 text-white" />
                          </div>
                          <span className="text-xs font-medium text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
                            {caseStudy.year}
                          </span>
                        </div>

                        {/* Client */}
                        <div className="text-sm font-medium text-primary-500 mb-1">
                          {caseStudy.client}
                        </div>

                        {/* Title */}
                        <h3 className="heading-4 mb-3 group-hover:text-primary-500 transition-colors">
                          {caseStudy.title}
                        </h3>

                        {/* Description */}
                        <p className="body-small text-gray-600 mb-4 line-clamp-3">
                          {caseStudy.description}
                        </p>

                        {/* Tags */}
                        <div className="flex flex-wrap gap-2 mb-4">
                          {caseStudy.tags.slice(0, 2).map((tag) => (
                            <span
                              key={tag}
                              className="text-xs bg-pink-50 text-primary-600 px-2 py-1 rounded-full border border-pink-100"
                            >
                              {tag}
                            </span>
                          ))}
                          {caseStudy.tags.length > 2 && (
                            <span className="text-xs text-gray-400 px-1">
                              +{caseStudy.tags.length - 2}
                            </span>
                          )}
                        </div>

                        {/* CTA */}
                        <div className="flex items-center text-sm font-medium text-primary-500 group-hover:text-primary-600 transition-colors">
                          {t('realizzazioni.discoverMore')}
                          <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                        </div>
                      </div>
                    </Card>
                  </motion.div>
                );
              })}
            </AnimatePresence>
          </motion.div>
        </Container>
      </Section>

      {/* Modal for Case Study Details */}
      <AnimatePresence>
        {selectedCase && (
          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSelectedCase(null)}
          >
            {/* Backdrop */}
            <div className="absolute inset-0 bg-gray-900/60 backdrop-blur-sm" />

            {/* Modal Content */}
            <motion.div
              className="relative bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header gradient */}
              <div
                className={cn(
                  'h-3 w-full bg-gradient-to-r rounded-t-2xl',
                  selectedCase.color
                )}
              />

              <div className="p-8">
                {/* Close button */}
                <button
                  onClick={() => setSelectedCase(null)}
                  className="absolute top-5 right-5 w-10 h-10 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors cursor-pointer"
                >
                  <span className="sr-only">{t('realizzazioni.modal.close')}</span>
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>

                {/* Icon and Meta */}
                <div className="flex items-center gap-4 mb-6">
                  <div
                    className={cn(
                      'w-16 h-16 rounded-2xl bg-gradient-to-br flex items-center justify-center',
                      selectedCase.color
                    )}
                  >
                    <selectedCase.icon className="w-8 h-8 text-white" />
                  </div>
                  <div>
                    <div className="text-sm font-medium text-primary-500">
                      {selectedCase.client}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      {t('realizzazioni.modal.projectYear')} {selectedCase.year}
                    </div>
                  </div>
                </div>

                {/* Title */}
                <h2 className="heading-2 mb-4">{selectedCase.title}</h2>

                {/* Description */}
                <p className="text-gray-600 mb-6 leading-relaxed">
                  {selectedCase.description}
                </p>

                {/* Results */}
                <div className="bg-pink-50/70 rounded-xl p-6 mb-6">
                  <h4 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-primary-500" />
                    {t('realizzazioni.modal.resultsTitle')}
                  </h4>
                  <ul className="space-y-3">
                    {selectedCase.results.map((result, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <CheckCircle2 className="w-5 h-5 text-primary-500 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">{result}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Tags */}
                <div className="flex flex-wrap gap-2">
                  {selectedCase.tags.map((tag) => (
                    <span
                      key={tag}
                      className="text-sm bg-gray-100 text-gray-700 px-3 py-1.5 rounded-full"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Partnerships Section */}
      <Section className="relative overflow-hidden" padding="lg">
        <div className="absolute inset-0 bg-gradient-to-br from-secondary-50/50 via-white to-pink-50/30" />

        <Container className="relative z-10">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <motion.span
              className="caption text-primary-400"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
            >
              {t('realizzazioni.partnerships.overline')}
            </motion.span>
            <motion.h2
              className="heading-2 mt-3"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              {t('realizzazioni.partnerships.title')}
            </motion.h2>
            <motion.p
              className="body-regular mt-3 text-gray-600"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              {t('realizzazioni.partnerships.description')}
            </motion.p>
          </div>

          <motion.div
            className="grid md:grid-cols-3 gap-6"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3 }}
          >
            {[
              {
                name: t('realizzazioni.partners.maggioli.name'),
                description: t('realizzazioni.partners.maggioli.description'),
                icon: Building2,
              },
              {
                name: t('realizzazioni.partners.apkappa.name'),
                description: t('realizzazioni.partners.apkappa.description'),
                icon: Zap,
              },
              {
                name: t('realizzazioni.partners.unipi.name'),
                description: t('realizzazioni.partners.unipi.description'),
                icon: Sparkles,
              },
            ].map((partner, index) => (
              <motion.div
                key={partner.name}
                className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 border border-gray-100 hover:border-pink-200 hover:shadow-lg transition-all duration-300"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.4 + index * 0.1 }}
                whileHover={{ y: -5 }}
              >
                <div className="w-12 h-12 bg-gradient-to-br from-primary-100 to-pink-100 rounded-xl flex items-center justify-center mb-4">
                  <partner.icon className="w-6 h-6 text-primary-500" />
                </div>
                <h3 className="heading-5 mb-2">{partner.name}</h3>
                <p className="body-small text-gray-600">{partner.description}</p>
              </motion.div>
            ))}
          </motion.div>
        </Container>
      </Section>

    </>
  );
}
